"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { searchFields } from "@/features/filters/filterDefs";
import {
  scopeParams,
  useItemsScope,
} from "@/features/items/hooks/useItemsScope";
import { itemDisplay } from "@/features/items/itemDisplay";
import { useMediaContext } from "@/features/media/useMediaContext";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { apiFetch } from "@/lib/api";
import type { ItemCollection } from "@/types";
import { useSearchMasks } from "./useSearchMasks";

/**
 * What the dropdown offers for what has been typed.
 *
 * Two kinds, because a project chooses between two ways of searching. A `term` is
 * a word that occurs in the collection, and picking one narrows the result list. A
 * `poi` is a place, and picking one opens it. `config.searchType` decides, and it
 * changes what the search *is* — not just how a row is drawn.
 */
export type Suggestion =
  | { kind: "term"; value: string; count: number }
  | { kind: "poi"; id: string; title: string };

/** How many suggestions the dropdown offers. */
const LIMIT = 5;

/**
 * Search suggestions for the current text.
 *
 * Term mode is scoped like the result list — the same bbox, terms and filters — so
 * every suggestion is one that would actually narrow what's on screen rather than
 * something that exists elsewhere in the collection. Which fields it draws on is
 * the backend's to decide: the collection carries the tenant's own full-text
 * declaration, weights and all, and repeating it from here would only be a chance
 * to disagree with it.
 *
 * POI mode is deliberately *not* scoped that way, as in legacy: it answers "where
 * is this place", so a POI outside the current viewport is exactly the answer the
 * user is after, and hiding it because the map happens to be elsewhere would defeat
 * the search. Filters are left off it for the same reason.
 */
export function useSuggestions(text: string) {
  const configs = useProjectConfigs();
  const scope = useItemsScope();
  const { masks } = useSearchMasks();
  const media = useMediaContext();

  // Only to decide whether term search is worth offering at all — a project with
  // no full-text index has no terms to suggest.
  const hasSearchableFields = useMemo(
    () => searchFields(configs).length > 0,
    [configs]
  );
  const query = text.trim();

  const { projectId, collectionId, lang, isReady } = scope;
  const isPOISearch = configs.searchType === "poi";
  const enabled = !!projectId && !!collectionId && isReady && !!query;

  const terms = useQuery({
    queryKey: [
      "suggestions",
      { projectId, collectionId, lang, q: query, filter: scope.filter, bbox: scope.bbox, search: scope.search },
    ],
    queryFn: ({ signal }) =>
      apiFetch<{ suggestions: Array<{ value: string; count: number }> }>(
        `/projects/${projectId}/collections/${collectionId}/items/suggestions`,
        {
          params: { ...scopeParams(scope), q: query, limit: LIMIT },
          lang,
          signal,
        }
      ),
    enabled: enabled && !isPOISearch && hasSearchableFields,
    // The dropdown shouldn't blank between keystrokes.
    placeholderData: (previous) => previous,
  });

  const pois = useQuery({
    queryKey: ["poi-suggestions", { projectId, collectionId, lang, q: query }],
    queryFn: ({ signal }) =>
      apiFetch<ItemCollection>(
        `/projects/${projectId}/collections/${collectionId}/items`,
        { params: { search: query, limit: LIMIT }, lang, signal }
      ),
    enabled: enabled && isPOISearch,
    placeholderData: (previous) => previous,
  });

  const suggestions: Suggestion[] = isPOISearch
    ? (pois.data?.features ?? []).map((item) => ({
        kind: "poi",
        id: item.id,
        title: itemDisplay(item, configs, media).title,
      }))
    : // A term already accepted as a chip is not worth offering again. POIs need
      // no such filter: picking one opens it rather than adding to the query, so
      // the same place can be offered again the next time it is searched for.
      (terms.data?.suggestions ?? [])
        .filter((suggestion) => !masks.includes(suggestion.value))
        .map((suggestion) => ({ kind: "term", ...suggestion }));

  return {
    suggestions,
    isPending: isPOISearch ? pois.isPending : terms.isPending,
    isError: isPOISearch ? pois.isError : terms.isError,
  };
}
